import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Car, 
  User, 
  FileText, 
  CreditCard, 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Clock,
  Info
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecapPage = () => {
  const navigate = useNavigate();
  const { dossierId } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const response = await axios.get(`${API}/dossiers/${dossierId}`);
        setDossier(response.data);
      } catch (error) {
        toast.error("Dossier non trouvé");
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [dossierId, navigate]);

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        dossier_id: dossierId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
      console.error(error);
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#000091] animate-spin" />
      </div>
    );
  }

  if (!dossier) return null;

  const price = dossier.package_type === 'premium' ? 149 : 99;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
              <p className="text-xs text-slate-500">Récapitulatif de votre demande</p>
            </div>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {['Formulaire', 'Signature', 'Documents', 'Récapitulatif'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${index < 3 ? 'bg-green-500 text-white' : 'bg-[#000091] text-white'}`}>
                  {index < 3 ? <CheckCircle className="w-5 h-5" /> : index + 1}
                </div>
                <span className={`text-sm hidden sm:inline ${index === 3 ? 'text-[#000091] font-medium' : 'text-slate-500'}`}>{step}</span>
              </div>
              {index < 3 && <div className="w-8 sm:w-16 h-0.5 bg-green-500 mx-2"></div>}
            </div>
          ))}
        </div>

        {/* Back button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(`/documents/${dossierId}`)}
            className="border-slate-300"
            data-testid="back-to-documents-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux documents
          </Button>
        </div>

        {/* Dossier number */}
        {dossier.numero_dossier && (
          <div className="mb-6 p-4 bg-[#000091]/5 border border-[#000091]/20 rounded-lg" data-testid="dossier-number-card">
            <p className="text-sm text-slate-600">Numéro de dossier</p>
            <p className="text-2xl font-bold text-[#000091]" data-testid="dossier-number">{dossier.numero_dossier}</p>
          </div>
        )}

        {/* Mobile Payment CTA - Fixed at bottom on mobile */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-50 shadow-lg" data-testid="mobile-payment-cta">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-[#0F172A]">Total TTC</span>
            <span className="text-xl font-bold text-[#000091]">{price}€</span>
          </div>
          <Button
            onClick={handlePayment}
            disabled={paymentLoading}
            className="w-full bg-[#000091] hover:bg-[#000091]/90 py-4"
            data-testid="mobile-pay-btn"
          >
            {paymentLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Redirection...
              </>
            ) : (
              <>
                Payer {price}€
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-32 lg:pb-0">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client info */}
            <Card className="border-slate-200" data-testid="client-recap-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-[#0F172A]">
                  <User className="w-5 h-5 text-[#000091]" />
                  Informations demandeur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Nom complet</span>
                    <p className="font-medium text-[#0F172A]">
                      {dossier.client.civilite} {dossier.client.prenom} {dossier.client.nom}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Date de naissance</span>
                    <p className="font-medium text-[#0F172A]">{dossier.client.date_naissance}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Adresse</span>
                    <p className="font-medium text-[#0F172A]">
                      {dossier.client.adresse}, {dossier.client.code_postal} {dossier.client.ville}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email</span>
                    <p className="font-medium text-[#0F172A]">{dossier.client.email}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Téléphone</span>
                    <p className="font-medium text-[#0F172A]">{dossier.client.telephone}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle info */}
            <Card className="border-slate-200" data-testid="vehicle-recap-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-[#0F172A]">
                  <Car className="w-5 h-5 text-[#000091]" />
                  Informations véhicule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Marque / Modèle</span>
                    <p className="font-medium text-[#0F172A]">
                      {dossier.vehicle.marque} {dossier.vehicle.modele}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">N° de série (VIN)</span>
                    <p className="font-medium text-[#0F172A] font-mono text-xs">
                      {dossier.vehicle.numero_serie}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Date 1ère immat.</span>
                    <p className="font-medium text-[#0F172A]">{dossier.vehicle.date_premiere_immat}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Pays d'origine</span>
                    <p className="font-medium text-[#0F172A]">{dossier.vehicle.pays_origine}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Énergie</span>
                    <p className="font-medium text-[#0F172A]">{dossier.vehicle.energie}</p>
                  </div>
                  {dossier.vehicle.puissance_fiscale && (
                    <div>
                      <span className="text-slate-500">Puissance fiscale</span>
                      <p className="font-medium text-[#0F172A]">{dossier.vehicle.puissance_fiscale} CV</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Récapitulatif de la demande */}
            <Card className="border-slate-200 border-[#000091] border-2" data-testid="demande-recap-card">
              <CardHeader className="pb-3 bg-[#000091]/5">
                <CardTitle className="flex items-center gap-2 text-lg text-[#000091]">
                  <CheckCircle className="w-5 h-5 text-[#000091]" />
                  Récapitulatif de votre demande
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">Formulaires CERFA prêts</p>
                      <p className="text-sm text-green-600">Les formulaires officiels (13757 et 13750) seront générés automatiquement et joints à votre dossier</p>
                    </div>
                  </div>
                  
                  {dossier.signature && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-green-800">Signature électronique enregistrée</p>
                        <p className="text-sm text-green-600">Votre signature sera apposée sur les documents officiels</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800">Traitement de votre dossier</p>
                      <p className="text-sm text-blue-600">Après paiement, notre équipe vérifiera vos documents et effectuera les démarches auprès de l'ANTS</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <p className="text-sm text-slate-600">
                      <strong>Formule choisie :</strong> {dossier.package_type === 'premium' ? 'WW Provisoire + Carte grise définitive' : 'Carte grise définitive'}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      <strong>Délai estimé :</strong> 1 à 5 semaines selon votre région
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="border-slate-200" data-testid="documents-recap-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-[#0F172A]">
                  <FileText className="w-5 h-5 text-[#000091]" />
                  Documents fournis ({Object.keys(dossier.documents || {}).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(dossier.documents || {}).map(docType => (
                    <span 
                      key={docType}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium"
                    >
                      <CheckCircle className="w-3 h-3" />
                      {docType.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Payment */}
          <div className="lg:col-span-1">
            <Card className="border-slate-200 sticky top-4" data-testid="payment-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-[#0F172A]">
                  <CreditCard className="w-5 h-5 text-[#000091]" />
                  Paiement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Frais de dossier</span>
                    <span className="font-medium text-[#0F172A]">
                      {dossier.package_type === 'premium' ? 'W + Définitive' : 'Définitive'}
                    </span>
                  </div>
                  <Separator className="my-3" />
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#0F172A]">Total TTC</span>
                    <span className="text-2xl font-bold text-[#000091]">{price}€</span>
                  </div>
                </div>

                {/* Warning about additional taxes */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700">
                      <p className="font-medium text-amber-800 mb-1">S'ajouteront également :</p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Taxe régionale (selon région et puissance)</li>
                        <li>Taxe de gestion (~11€)</li>
                        <li>Malus écologique (si applicable)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Delivery time info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium text-blue-800">Délais de traitement</p>
                      <p className="mt-1">
                        Le délai de traitement des dossiers définitifs varie de <strong>1 à 5 semaines</strong> selon la région.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={paymentLoading}
                  className="w-full bg-[#000091] hover:bg-[#000091]/90 py-6"
                  data-testid="pay-btn"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirection...
                    </>
                  ) : (
                    <>
                      Payer {price}€
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                  </svg>
                  Paiement sécurisé par Stripe
                </div>

                {/* CGV Link */}
                <p className="text-xs text-center text-slate-500">
                  En payant, vous acceptez nos{' '}
                  <a href="/cgv" target="_blank" className="text-[#000091] hover:underline">
                    Conditions Générales de Vente
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RecapPage;
